import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import paths from "../../../utils/constants";
import { BreadCrumbSeparatorIcon, FireIcon, MilestoneCheckIcon, MilestoneLockIcon, StreakCheckIcon, StreakDiamondIcon, StreakLockIcon, StreakOngoingIcon } from "./Icons";
import api from "../../../api/api";
import { logMessage } from "../../../utils/helpers";
import { cn } from "../../../lib/utils";

const PURPLE = "#552F83";
const PAGE_BG = "#F1ECFF";

function isApiSuccess(res) {
  if (res == null) return false;
  const s = res.status;
  return s === 1 || s === "1" || Number(s) === 1;
}

const StreaksJourneyPanel = ({startLoading, stopLoading}) => {
    const navigate = useNavigate();
    const [payload, setPayload] = useState(null);

    const loadersRef = useRef({ startLoading, stopLoading });
    loadersRef.current = { startLoading, stopLoading };

  const load = useCallback(async () => {
    const { startLoading: sl, stopLoading: el } = loadersRef.current;
    sl?.();
    try {
      const [dashOutcome] = await Promise.allSettled([
        api.getStreaks({}),
      ]);

      const res =
        dashOutcome.status === "fulfilled" ? dashOutcome.value : null;

      let normalized;
      if (res && isApiSuccess(res) && res.data) {
        normalized = res.data;
      } else {
        if (dashOutcome.status === "rejected") {
          logMessage(dashOutcome.reason);
        } else {
          logMessage(`getPointsDashboard non-success: ${res?.message || ""}`, "warn");
        }
        normalized = res.data;
      }

      setPayload(normalized);
    } catch (e) {
      logMessage(e);
      setPayload();
    } finally {
      el?.();
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  console.log("Streaks is streaks: ", payload);

    return (
        <div
      className="relative  min-h-full "
      style={{ backgroundColor: PAGE_BG }}
    >
  {/* Header Section */}
  <div
    className="relative overflow-hidden rounded-t-xl text-white pb-8" // Added pb-8 for consistent bottom spacing
    style={{
      backgroundImage: "url('/bgImage.webp')",
      backgroundSize: "cover",
      backgroundPosition: "center",
    }}
  >
    {/* Breadcrumbs - Reduced margin-bottom */}
    <div className="relative z-10 px-4 pt-4">
      <p className="absolute text-[12px] text-white mb-2 drop-shadow-sm flex items-center">
        <button
          type="button"
          className="cursor-pointer hover:underline text-white bg-transparent border-0 p-0 font-inherit"
          onClick={() => navigate(paths.YOUR_PROGRESS)}
        >
          Your progress
        </button>
        <BreadCrumbSeparatorIcon className="px-1.5 size-2.5" />
        <span className="font-medium text-white">Streaks</span>
      </p>

      {/* Main Hero Content - Reduced gap from gap-2 to gap-1 */}
      <div className="w-full flex flex-col items-center justify-center gap-2 mt-6 border-[2px] border-white">
        <span className="text-white text-[24px] font-semibold">
          Your Painting Streak <FireIcon />
        </span>
        <span className="text-base font-bold text-white/90 pt-2">
          Consistency is your masterpiece.
        </span>
        
        {/* Streak Number - Reduced margin and tightened leading */}
        <p className="text-white text-[48px] font-bold leading-none my-2">
          {payload?.current_streak_text}
        </p>
        
        {/* Milestone Message - Added max-width to prevent horizontal overflow */}
        <p className="max-w-[400px] text-white font-semibold text-sm leading-tight text-center opacity-90">
          {payload?.milestone_message}
        </p>
      </div>
    </div>
  </div>

  {/* Content Section */}
  <div className="relative px-6">
    <div className="w-full flex flex-col mt-0">
    {/* Info Row - Improved spacing and wrapping */}
    <div className="w-full flex items-center justify-between gap-4 my-2">
      <div className="flex flex-col gap-2 pb-3"> {/* flex-1 helps text wrap instead of pushing width */}
        <span className="text-[#323232] font-semibold text-base">
          Diamond Streak
        </span>
        <span className="text-[#323232] font-normal text-sm">
          One session a day keeps your streaks shining
        </span>
      </div>
      
      {/* XP Tag - Fixed px and whitespace-nowrap to prevent squishing */}
      <div className="bg-white/50 whitespace-nowrap w-auto px-3 py-1 text-black text-xs font-normal rounded-full mr-4">
        {payload?.xp_daily_activity_text}
      </div>
    </div>

    <div 
        className={cn(
            "bg-white w-full flex flex-wrap items-center justify-center gap-4 py-6 rounded-[32px]",
            // Logic: Determine max width to force row breaks
           // Fits 10 in a row
        )}
        >
         <div className="w-full flex items-center justify-center">
        <div className={cn("bg-white flex flex-wrap items-center justify-start gap-4 px-3.5",  
           payload?.timeline?.length <= 10 
        ? "max-w-[350px]" // Fits exactly 5 items (48px * 5 + 16px * 4)
        : "max-w-[680px] justify-start"  )}>
            {payload?.timeline?.map((item, index) => (
            <div 
            key={index} 
            className={cn(
                "size-12 rounded-full flex items-center justify-center transition-all",
                item.status === "completed" ? "bg-[#552F83]" : item.status === "graced" ? "bg-[#DEDEDE]" : "bg-white !border-[2px] border-[#DEDEDE]" // Gray for upcoming
            )}
             style={{ border: `2px solid ${item.status === "completed" ? "#552F83" : "#DEDEDE"}` }}
            >
            <StreakDiamondIcon 
                width={26}
                height={26}
                className={cn(
                "size-7.5",
                    item.status === "completed" ? "opacity-100 text-white" : item.status === "upcoming" ? "opacity-100 text-[#DEDEDE]" : "text-white opacity-100" 
                )} 
            />
            </div>
        ))}        
       </div>  
       </div>   

        <div className="w-full flex items-center justify-center gap-4 pb-0">
            {payload?.streak_segments?.map((streak)=>(
                <div key={streak?.name} className={cn("w-[100px] h-[33px] rounded-full flex items-center justify-center text-center border-1 border-[#DEDEDE] gap-2", streak?.status === "completed" && "bg-[#552F83]")}
                style={{border: `1px solid ${streak.status === 'ongoing' ? '#B58F08' : streak?.status === "upcoming" ? '#DEDEDE' : "#552F83"}`}}
                >
                    {streak.status === "completed" ? <StreakCheckIcon /> : streak?.status === "ongoing" ? <StreakOngoingIcon /> :<StreakLockIcon />}
                    <span className={cn("font-medium text-sm text-[#DEDEDE]", streak?.status === "completed" ? "text-white" : streak?.status === "ongoing" ? "text-[#B58F08]" : "text-[#DEDEDE]")}>{streak?.days_label}</span>
                </div>
            ))}
        </div>   
        {payload?.grace_days_used_text && (
            <span className="w-full flex items-center justify-center text-[#F1474C] font-medium text-base leading-none p-0">
                {payload?.grace_days_used_text}
            </span>
        )}

        {/* <div className="w-full" style={{border: "0.5px solid #DEDEDE"}}></div> */}
        <hr className="w-[96%] border-t-[0.5px] border-[#DEDEDE]/30 my-2 mx-auto" />

        <div className="w-full flex items-center justify-center gap-14">
            <div className="w-auto flex gap-3 items-center justify-center">
                <div className="size-[20px] rounded-full flex items-center justify-center bg-[#552F83]">
                    <StreakDiamondIcon width={11} height={11} className="text-white"/>
                </div>
                <span className="text-[#323232] font-medium text-xs">Painted</span>
            </div>
            <div className="w-auto flex gap-3 items-center justify-center">
                <div className="size-[20px] rounded-full flex items-center justify-center bg-[#DEDEDE]">
                    <StreakDiamondIcon width={11} height={11} className="text-white"/>
                </div>
                <span className="text-[#323232] font-medium text-xs">Grace</span>
            </div>
            <div className="w-auto flex gap-3 items-center justify-center">
                <div 
                  className="size-[20px] rounded-full flex items-center justify-center bg-white"
                  style={{ border: "1px solid #DEDEDE"}}
                >
                    <StreakDiamondIcon width={11} height={11} className="text-[#DEDEDE]"/>
                </div>
                <span className="text-[#323232] font-medium text-xs">Upcoming</span>
            </div>
        </div>   
        </div>

     <div 
        className={cn(
        "bg-white w-full flex flex-wrap items-center gap-4 py-3 rounded-[19px] mt-4")}
        style={{border: "1px solid #DADADA"}}
     >
        <div className="w-full flex items-center gap-4 px-4">
            <div className="size-[60px] bg-[#DEDEDE] rounded-full flex items-center justify-center">
                <StreakDiamondIcon width={35} height={35} className="text-white"/>
            </div>
            <div className="flex flex-col items-start gap-1.5">
                <span className="text-base font-semibold text-[#552F83] !leading-none">Grace Days</span>
                <span className="text-xs text-[#323232]">{payload?.grace_days?.availability_text}</span>
                <span className="text-xs text-[#323232] leading-tight">{payload?.grace_days?.help_text}</span>
            </div>
        </div>
      </div>   
      <div className="w-full flex flex-col gap-2 mt-6">
            <span className="text-black font-bold text-[16px]">Rewards Journey</span>
            <span className="text-[#323232] font-normal text-sm">Rewards are applied automatically when milestones are reached.</span>
        </div>
      <div className="relative flex flex-col items-start justify-start mt-3 gap-4 mb-5 px-4">
        <div className="w-full flex flex-col items-center justify-center gap-4">
            {payload?.milestones?.map((milestone)=>(
                <div key={milestone?.title} className="w-full bg-white rounded-[9px] h-[54px] bg-[linear-gradient(273.25deg, #FFFFFF -14.44%, #EEECFF 241.07%)] drop-shadow-xl flex items-center justify-between px-4">
                    <span className="text-[#323232] text-sm">{milestone?.title}</span>
                    {milestone?.status === "completed" ? <MilestoneCheckIcon /> : <MilestoneLockIcon />}
                </div>
            ))}
        </div>
      </div>
  </div>
  </div>
</div>
    )
}

export default StreaksJourneyPanel;